import { useEffect, useRef } from 'react'
import { useHomepageRanking } from '../queries/useHomepageRanking.js'
import { useUpdateGoal } from '../mutations/useUpdateGoal.js'

/**
 * useGscGoalSync — detects goals that track Search Console rankings
 * and auto-writes the live position into goal.current.
 *
 * Trigger: goal name matches /homepage\s*rank/i. This avoids needing a
 * schema change on stored goals. If we later want multi-page tracking
 * we can replace name detection with a `gscSync: 'homepage'` field.
 *
 * Write semantics:
 *   - Fires once per data refresh, guarded by a ref so we don't loop
 *   - Only writes if the rounded position differs from current
 *   - Writes as a number (rounded to 1 decimal) so progress bar math works
 *   - Also sets `lowerIsBetter: true` once, so calcPct treats "lower rank
 *     = better progress toward target 10"
 */
export function isGscHomepageGoal(goal) {
  return Boolean(goal?.goal && /homepage\s*rank/i.test(goal.goal))
}

export function useGscGoalSync({ enabled, pillarName, goalIndex, goal }) {
  const { data, isLoading } = useHomepageRanking()
  const mutation = useUpdateGoal()
  const lastWrittenRef = useRef(null)

  useEffect(() => {
    if (!enabled || isLoading) return
    if (!data || !data.hasData || data.position == null) return

    const next = Number(Number(data.position).toFixed(1))
    // Skip if we already wrote this value OR current already matches
    if (lastWrittenRef.current === next) return
    const currentNum = Number(goal?.current)
    if (!Number.isNaN(currentNum) && Math.abs(currentNum - next) < 0.05) {
      lastWrittenRef.current = next
      return
    }

    lastWrittenRef.current = next
    mutation.mutate({ pillarName, goalIndex, field: 'current', value: next })

    // If the goal doesn't yet have lowerIsBetter set, flip it. One-shot
    // write, guarded so we don't spam.
    if (!goal?.lowerIsBetter) {
      mutation.mutate({ pillarName, goalIndex, field: 'lowerIsBetter', value: true })
    }
  }, [
    enabled,
    isLoading,
    data?.position,
    data?.hasData,
    goal?.current,
    goal?.lowerIsBetter,
    pillarName,
    goalIndex,
  ])

  return { data, isLoading }
}
